import { Descriptions, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { DateTimeCell, MonoValue, RevokedStatusTag } from 'src/components'

const { Text } = Typography

/**
 * Minimum credential shape used for the metadata Descriptions block. Both
 * `AppCredentialRead` and `PublicAccessCredentialRead` satisfy this — they
 * share exactly the same set of top-level "who / when / status" fields.
 */
export type CredentialMetadataView = {
  id: string
  name: string
  status?: string
  expires_at?: string | null
  last_used_at?: string | null
  created_at: string
  created_by?: { email?: string | null } | null
  revoked_at?: string | null
  reason?: string | null
}

const statusColor = (status: string) => {
  if (status === 'active') return 'success'
  if (status === 'revoked') return 'error'
  if (status === 'expired') return 'warning'
  return 'default'
}

type Props = {
  credential: CredentialMetadataView
}

/**
 * Shared "who / when / status" block for credential detail pages. Renders as
 * a responsive AntD Descriptions grid, following the global typography rule
 * (labels are the theme's 12/500/secondary, never overridden here).
 */
export const CredentialMetadataDescriptions = ({ credential }: Props) => {
  const { t } = useTranslation()
  const status = credential.status ?? 'active'

  const statusNode =
    status === 'revoked' ? (
      <RevokedStatusTag
        item={credential}
        reasonTip={(reason) =>
          t('workspace.credentials.revokedReason', { reason })
        }
        atTip={(at) => t('workspace.credentials.revokedAt', { at })}
      >
        <Tag color="error" style={{ marginInlineEnd: 0 }}>
          {t('workspace.credentials.status.revoked')}
        </Tag>
      </RevokedStatusTag>
    ) : (
      <Tag color={statusColor(status)} style={{ marginInlineEnd: 0 }}>
        {t(`workspace.credentials.status.${status}`)}
      </Tag>
    )

  return (
    <Descriptions
      column={{ xs: 1, sm: 2, lg: 3 }}
      size="small"
      items={[
        {
          key: 'name',
          label: t('name'),
          children: <Text>{credential.name}</Text>
        },
        {
          key: 'status',
          label: t('workspace.credentials.columns.status'),
          children: statusNode
        },
        {
          key: 'expires',
          label: t('workspace.credentials.columns.expires'),
          children: (
            <DateTimeCell
              value={credential.expires_at}
              variant="dateTime"
              empty={t('workspace.credentials.noExpiry')}
            />
          )
        },
        {
          key: 'lastUsed',
          label: t('workspace.credentials.columns.lastUsed'),
          children: (
            <DateTimeCell
              value={credential.last_used_at}
              variant="relative"
              empty={t('workspace.credentials.neverUsed')}
            />
          )
        },
        {
          key: 'created',
          label: t('workspace.credentials.columns.created'),
          children: (
            <DateTimeCell value={credential.created_at} variant="dateTime" />
          )
        },
        {
          key: 'createdBy',
          label: t('workspace.credentials.columns.createdBy'),
          children: credential.created_by?.email ? (
            <Text>{credential.created_by.email}</Text>
          ) : (
            <Text type="secondary">—</Text>
          )
        },
        {
          key: 'id',
          label: t('workspace.credentials.columns.id'),
          children: <MonoValue ellipsis>{credential.id}</MonoValue>
        }
      ]}
    />
  )
}
